import { lazy, Suspense, useEffect, useMemo } from 'react'
import { Switch, Route, useParams, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  getRootPlans as getRootPlansAction,
  clearPlans as clearPlansAction
} from '../../PlanSelection/actions'
import {
  getProjectDetails as getProjectDetailsAction,
  clearProjectDetails as clearProjectDetailsAction
} from '../../ProjectSettings/actions'
import {
  makeSelectProjectDetails,
  makeSelectGetProjectDetailsError
} from '../../ProjectSettings/selectors'
import { makeSelectGetPlanRevisionError } from '../../Plan/selectors'
import {
  Loader,
  NoBgLoaderWrapper
} from '../../../components/page-loader/styled-components'
import { AsyncLoaderPage } from '../../../components/async-loader/async-loader-page'
import { isEmpty } from 'lodash'
import { makeSelectRootPlans } from '../../PlanSelection/selectors.js'
import { useGetProjectMembersQuery } from '../../../react-query/members/query/use-get-project-members-query.jsx'
import { useGetProjectTeamsQuery } from '../../../react-query/members/query/use-get-project-teams-query.jsx'
import { useGetProjectContactsQuery } from '../../../react-query/members/query/use-get-project-contacts-query.jsx'
import PlanMenu from '../../plan-menu/index.jsx'

const PlanViewerNew = lazy(() => import('../../PlanViewerNew'))
const TeamManagement = lazy(() => import('../../TeamsManagement'))
const TaskList = lazy(() => import('../../TaskList'))
const Datasets = lazy(() => import('../../Datasets'))
const Media = lazy(() => import('../../Media'))
const Reports = lazy(() => import('../../Reports'))
const Groups = lazy(() => import('../../Groups'))
const Group = lazy(() => import('../../Group'))
const PlanVersions = lazy(() => import('../../PlanVersions'))
const NotFoundPage = lazy(() => import('../../NotFoundPage'))

const ProjectRouter = () => {
  const { projectId } = useParams()
  const history = useHistory()
  const dispatch = useDispatch()

  // Seletors
  const plans = useSelector(makeSelectRootPlans())
  const projectDetails = useSelector(makeSelectProjectDetails())
  const projectDetailsError = useSelector(makeSelectGetProjectDetailsError())
  const planRevisionError = useSelector(makeSelectGetPlanRevisionError())

  // projectId comes straight from the route param, so it can be a
  // non-project value (e.g. a stale "/project/upgrade" link). Only
  // pre-fetch project-scoped data once the project itself is confirmed.
  const isConfirmedProject = !!projectDetails && projectDetails.id === projectId

  // pre-fetch assignees for tasks
  useGetProjectMembersQuery(
    projectId,
    '',
    { size: 100 },
    { enabled: isConfirmedProject }
  )
  useGetProjectContactsQuery(projectId, '', { enabled: isConfirmedProject })
  useGetProjectTeamsQuery(projectId, '', { enabled: isConfirmedProject })

  // actions
  const getRootPlans = (...attributes) =>
    dispatch(getRootPlansAction(...attributes))
  const clearPlans = (...attributes) => {
    dispatch(clearPlansAction(...attributes))
  }
  const getProjectDetails = (...attributes) =>
    dispatch(getProjectDetailsAction(...attributes))
  const clearProjectDetails = (...attributes) =>
    dispatch(clearProjectDetailsAction(...attributes))

  // UseEffects
  useEffect(() => {
    if (projectId) {
      if (
        isEmpty(plans) ||
        !projectDetails ||
        projectDetails.id !== projectId
      ) {
        clearPlans()
        getRootPlans(projectId, 0, 'Title', 'Active', '')
        if (!projectDetails || projectDetails.id !== projectId) {
          clearProjectDetails()
          getProjectDetails(projectId)
        }
      }
    }
  }, [projectId, projectDetails])

  useEffect(() => {
    if (
      (!!projectDetailsError && projectDetailsError.status === 403) ||
      planRevisionError?.status === 403
    ) {
      history.push('/project', { data: 'permission denied' })
    }
  }, [projectDetailsError, planRevisionError])

  // consts
  const teamRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/team/importStatus',
        component: TeamManagement,
        exact: true
      },
      {
        path: '/project/:projectId/team/:viewType/importStatus',
        component: TeamManagement,
        exact: true
      },
      { path: '/project/:projectId/team/:viewType?', component: TeamManagement }
    ],
    []
  )

  const taskRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/tasks/importStatus',
        component: TaskList,
        exact: true
      },
      {
        path: '/project/:projectId/tasks/new',
        component: TaskList,
        exact: true
      },
      { path: '/project/:projectId/tasks', component: TaskList, exact: true }
    ],
    []
  )

  const datasetRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/datasets/importStatus',
        component: Datasets,
        exact: true
      },
      {
        path: '/project/:projectId/datasets/:datasetId/importStatus',
        component: Datasets,
        exact: true
      },
      {
        path: '/project/:projectId/datasets/:datasetId',
        component: Datasets,
        exact: true
      },
      { path: '/project/:projectId/datasets', component: Datasets, exact: true }
    ],
    []
  )

  const mediaRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/media/importStatus',
        component: Media,
        exact: true
      },
      { path: '/project/:projectId/media', component: Media }
    ],
    []
  )

  const reportRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/reports/importStatus',
        component: Reports,
        exact: true
      },
      { path: '/project/:projectId/reports', component: Reports }
    ],
    []
  )

  const groupRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/groups/importStatus',
        component: Groups,
        exact: true
      },
      {
        path: '/project/:projectId/groups/:groupId/:viewType/:planId?/:pinId?/:tab?/:pinOption?/:page?/:size?/:search?/:option?/:subOption?/:id?',
        component: Group
      },
      { path: '/project/:projectId/groups', component: Groups, exact: true }
    ],
    []
  )

  const planVersionRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/planVersions/:planId/importStatus',
        component: PlanVersions,
        exact: true
      },
      {
        path: '/project/:projectId/planVersions/:planId',
        component: PlanVersions,
        exact: true
      }
    ],
    []
  )

  const pinRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/pins/:viewType/noPlan/:pinId?/:tab?/:pinOption?/:page?/:size?/:search?',
        component: PlanViewerNew
      },
      {
        path: '/project/:projectId/pins/:viewType/:planId?/:pinId?/:tab?/:pinOption?/:page?/:size?/:search?/:option?/:subOption?/:id?',
        component: PlanViewerNew
      },
      {
        path: '/project/:projectId/pins/:viewType/:planId?/:pinId?/:tab?/:pinOption?/:search?',
        component: PlanViewerNew
      }
    ],
    []
  )

  const importRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/importRevision/:planId/:option?',
        component: PlanMenu,
        exact: true
      },
      {
        path: '/project/:projectId/importStatus',
        component: PlanMenu,
        exact: true
      },
      {
        path: '/project/:projectId/import/:destinationFolderId?',
        component: PlanMenu,
        exact: true
      }
    ],
    []
  )

  const commonProjectRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/move',
        component: PlanMenu
      },
      {
        path: '/project/:projectId/newfolder/:parentId?',
        component: PlanMenu,
        exact: true
      },
      {
        path: '/project/:projectId/rename/:planId',
        component: PlanMenu,
        exact: true
      },
      {
        path: '/project/:projectId/exportPlan/:planId',
        component: PlanMenu,
        exact: true
      },
      // Plan routes need to cover deeper nested paths such as task details.
      {
        path: '/project/:projectId/:planId/:viewType/:pinId?/:tab?/:pinOption?/:page?/:size?/:search?/:option?/:subOption?/:id?',
        component: PlanViewerNew
      },
      {
        path: '/project/:projectId/:planId/:viewType/:pinId?/:tab?/:pinOption?/:search?',
        component: PlanViewerNew
      },
      { path: '/project/:projectId', component: PlanMenu, exact: true }
    ],
    []
  )

  if (!projectDetails || projectDetails.id !== projectId) {
    return (
      <NoBgLoaderWrapper>
        <Loader className="loader" />
      </NoBgLoaderWrapper>
    )
  }

  return (
    <>
      <Suspense fallback={<AsyncLoaderPage />}>
        <Switch>
          {/* Todas as rotas em ordem de especificidade */}
          {teamRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {taskRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {datasetRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {mediaRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {reportRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {groupRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {planVersionRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {pinRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {importRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {commonProjectRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          <Route component={NotFoundPage} />
        </Switch>
      </Suspense>
    </>
  )
}

export default ProjectRouter
